import { Product } from "@omni/types/api/product/bff/product.model";
import { DeliveryType } from "@omni/types/api/product/bff/shared/enums/deliveryType.enum";
import { ProductFieldSet } from "@omni/types/api/product/bff/shared/enums/productFieldSet.enum";
import { PageTemplate } from "@omni/types/enums/pageTemplate.enum";
import { isDefined } from "@omni/utils/helper.utils";
import { cn } from "@omni/utils/styles";
import React, { Suspense, SyntheticEvent, useMemo, useState } from "react";

import ProductTileButtonComponent from "@/components/Product/ProductTile/components/button/productTileButton.component";
import { useSearchHitContext } from "@/components/Search/SearchResults/StateComponents/ResultList/context/searchHit.context";
import { useThunkDispatch } from "@/redux/dispatcher";
import { CartModel } from "@/redux/modules/api/data/cart/@types/cart.model";
import * as CartModule from "@/redux/modules/api/data/cart/cart.module";
import { ModalType } from "@/redux/modules/value/modal/modal.types";
import * as AlgoliaTrackingService from "@/services/algolia/algoliaTracking.service";
import { useModalRouter } from "@/utils/hooks/useModalRouter";
import { isPageTemplate } from "@/utils/pageTemplate.utils";

const LazyAddToCartModal = React.lazy(() =>
    import("@/components/Modals/AddToCartModal/addToCartModal.container").then(
        ({ AddToCartModal }) => ({
            default: AddToCartModal
        })
    )
);

type InstantAddToCartProductTileButtonProps = {
    product: Product<ProductFieldSet.ProductTile>;
    extraOnClick: (e: SyntheticEvent<HTMLButtonElement>) => void;
    cart?: CartModel | null;
    className?: string;
    defaultDeliveryMethod: DeliveryType | null;
    defaultQuantity: number | null;
    preferredStore?: string;
};

const InstantAddToCartProductTileButton = ({
    product,
    cart,
    extraOnClick,
    className,
    defaultDeliveryMethod,
    defaultQuantity,
    preferredStore
}: InstantAddToCartProductTileButtonProps) => {
    const [addToCartLoading, setAddToCartLoading] = useState(false);
    const productInCart = useMemo(() => {
        return cart?.items?.find((item) => item.sku === product.sku);
    }, [cart, product]);
    const dispatch = useThunkDispatch();

    const { open: openModal } = useModalRouter(
        ModalType.AddToCart,
        product.sku
    );

    const { position, queryID } = useSearchHitContext();

    const handleOnClick = (e: SyntheticEvent<HTMLButtonElement>) => {
        e.preventDefault();
        e.stopPropagation();

        if (defaultDeliveryMethod && defaultQuantity && !productInCart) {
            setAddToCartLoading(true);
            if (isDefined(queryID) && isDefined(position)) {
                AlgoliaTrackingService.trackQuery(
                    queryID,
                    product.sku,
                    position
                );
            }

            return dispatch(
                CartModule.addProductTileProductToCart(
                    {
                        deliveryType: defaultDeliveryMethod,
                        quantity: defaultQuantity,
                        shopId: preferredStore,
                        sku: product.sku
                    },
                    product
                )
            ).then(() => {
                setAddToCartLoading(false);
                if (!isPageTemplate(PageTemplate.ShoppingCart)) {
                    openModal(false);
                }
            });
        }

        return extraOnClick(e);
    };

    return (
        <>
            <ProductTileButtonComponent
                onClick={handleOnClick}
                showOutline={!!productInCart}
                className={cn("instantAddToCardProductTileButton", className)}
            >
                <i
                    className={cn("las la-lg", {
                        "la-plus": !productInCart && !addToCartLoading,
                        "la-check": productInCart && !addToCartLoading,
                        "la-spinner la-pulse": addToCartLoading
                    })}
                ></i>
                <i className="las la-lg la-shopping-cart"></i>
            </ProductTileButtonComponent>
            <Suspense>
                {isDefined(product.price?.currentPrice) && (
                    <LazyAddToCartModal
                        sku={product.sku}
                        title={product.title}
                        medias={product.medias ?? []}
                        boxArticle={product.boxArticle}
                        additionalService={product.isAdditionalService}
                        promotions={product.price?.promotions || []}
                        salesIncentives={product.salesIncentives}
                        visibilityType={product.productVisibilityType}
                        currentPrice={product.price?.currentPrice}
                        strikethroughPrice={product.price?.strikethroughPrice}
                    />
                )}
            </Suspense>
        </>
    );
};

export default InstantAddToCartProductTileButton;
